// material ui
import { GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-premium';

import { DateTime } from 'luxon';

const getTop15Value = (data) => {
  const top15 = [];
  for (let i = 0; i < 15; i++) {
    let biggest = null;
    for (const item of data) {
      if (
        !top15.includes(item) &&
        (biggest === null || item.average_all > biggest.average_all)
      ) {
        biggest = item;
      }
    }
    if (biggest) {
      top15.push(biggest);
    }
  }

  return Math.floor(top15[top15.length - 1]?.average_all * 100) / 100;
};

export const presetKeys = [
  'overview',
  'dailyAnalysis',
  'trendAnalysis',
  'calendar_totals',
  'temperature_analysis',
  'price_analysis',
  'detailed',
];

export const getPresets = (transformedData, t, analysisType) => {
  const current_date = DateTime.fromISO(
    transformedData?.summed_data?.[transformedData?.summed_data?.length - 1]
      ?.timestamp,
  );
  const current_year = current_date.isValid ? current_date.year : null;
  const last_year = current_year ? current_year - 1 : null;
  const previous_year = current_year ? current_year - 2 : null;

  if (analysisType === 'power')
    return [
      {
        name: t('presets.overview'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'average_all',
          'average_all_inactive_hours',
          'total_consumption_year',
          'total_consumption_last_year',
          'total_consumption_previous_year',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'average_all' },
              { field: 'average_all_inactive_hours' },
            ],
          },
          {
            groupId: 'yearlyTotal',
            description: t('columnGroups.yearlyTotal.description'),
            headerName: t('columnGroups.yearlyTotal.header'),
            children: [
              { field: 'total_consumption_year' },
              { field: 'total_consumption_last_year' },
              { field: 'total_consumption_previous_year' },
            ],
          },
        ],
      },
      {
        name: t('presets.dailyAnalysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'average_all',
          'average_17_to_21',
          'average_weekday_day',
          'average_all_inactive_hours',
          'inactive_active_ratio',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [
            {
              field: 'average_all',
              operator: '>=',
              value: getTop15Value(transformedData.portfolio_analysis_list),
            },
          ],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'average_all' },
              { field: 'average_17_to_21' },
              { field: 'average_weekday_day' },
              { field: 'average_all_inactive_hours' },
            ],
          },
          {
            groupId: 'consumptionRatio',
            headerName: t('columnGroups.consumptionRatio.header'),
            description: t('columnGroups.consumptionRatio.description'),
            children: [{ field: 'inactive_active_ratio' }],
          },
        ],
      },
      {
        name: t('presets.trendAnalysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'average_all',
          'average_all_last_year',
          'yearly_trend',
          'yearly_trend_active',
          'yearly_trend_inactive',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'average_all' },
              { field: 'average_all_last_year' },
            ],
          },
          {
            groupId: 'yearly_trend',
            headerName: t('columnGroups.yearly_trend.header'),
            description: t('columnGroups.yearly_trend.description'),
            children: [
              { field: 'yearly_trend' },
              { field: 'yearly_trend_active' },
              { field: 'yearly_trend_inactive' },
            ],
          },
        ],
      },
      {
        name: t('presets.calendar_totals'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'previous_calendar_year_sum',
          'previous_calendar_year_consumption_pr_m2_pr_y',
          'last_calendar_year_sum',
          'last_calendar_year_consumption_pr_m2_pr_y',
          'current_calendar_year_sum',
          'current_calendar_year_consumption_pr_m2_pr_y',
          'warnings',
        ],
        sortingModel: [{ field: GRID_TREE_DATA_GROUPING_FIELD, sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'previous_calendar_year',
            description: t('columnGroups.previous_calendar_year.description'),
            headerName: previous_year
              ? previous_year.toString()
              : t('columnGroups.previous_calendar_year.header'),
            children: [
              { field: 'previous_calendar_year_sum' },
              { field: 'previous_calendar_year_consumption_pr_m2_pr_y' },
            ],
          },
          {
            groupId: 'last_calendar_year',
            description: t('columnGroups.last_calendar_year.description'),
            headerName: last_year
              ? last_year.toString()
              : t('columnGroups.last_calendar_year.header'),
            children: [
              { field: 'last_calendar_year_sum' },
              { field: 'last_calendar_year_consumption_pr_m2_pr_y' },
            ],
          },
          {
            groupId: 'current_calendar_year',
            description: t('columnGroups.current_calendar_year.description'),
            headerName: current_year
              ? current_year.toString()
              : t('columnGroups.current_calendar_year.header'),
            children: [
              { field: 'current_calendar_year_sum' },
              { field: 'current_calendar_year_consumption_pr_m2_pr_y' },
            ],
          },
        ],
      },
      {
        name: t('presets.temperature_analysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'forward_flow_temp_avg_last_30_days',
          'return_flow_temp_avg_last_30_days',
          'forward_flow_temp_avg_year',
          'return_flow_temp_avg_year',
          'delta_temp_avg_last_30_days',
          'delta_temp_avg_year',
          'delta_temp_trend_last_30_days',
          'delta_temp_trend_year',
          'warnings',
        ],
        sortingModel: [{ field: 'delta_temp_avg_last_30_days', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },

        columnGroupingModel: [
          {
            groupId: 'delta_temperature',
            description: t('columnGroups.delta_temperature.description'),
            headerName: t('columnGroups.delta_temperature.header'),
            children: [
              { field: 'delta_temp_avg_last_30_days' },
              { field: 'delta_temp_avg_year' },
              { field: 'delta_temp_trend_last_30_days' },
              { field: 'delta_temp_trend_year' },
            ],
          },
        ],
      },
      {
        name: t('presets.price_analysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'average_total_price',
          'average_nighttime_price',
          'total_cost_year',
          'total_cost_last_year',
          'total_cost_previous_year',
          'warnings',
        ],
        sortingModel: [{ field: 'average_total_price', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averagePrice',
            headerName: t('columnGroups.averagePrice.header'),
            description: t('columnGroups.averagePrice.description'),
            children: [
              { field: 'average_total_price' },
              { field: 'average_nighttime_price' },
            ],
          },
          {
            groupId: 'yearlyTotalPrice',
            description: t('columnGroups.yearlyTotalPrice.description'),
            headerName: t('columnGroups.yearlyTotalPrice.header'),
            children: [
              { field: 'total_cost_year' },
              { field: 'total_cost_last_year' },
              { field: 'total_cost_previous_year' },
            ],
          },
        ],
      },
      {
        name: t('presets.detailed'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'occupancy',
          'daytime_start',
          'daytime_end',
          'average_all',
          'average_all_last_year',
          'current_calendar_year_avg',
          'last_calendar_year_avg',
          'average_last_3_years',
          'average_17_to_21',
          'average_all_inactive_hours',
          'average_weekday_full_day',
          'average_weekend_full_day',
          'average_weekday_day',
          'average_weekend_day',
          'average_weekday_night',
          'average_weekend_night',
          'total_consumption_year',
          'total_consumption_last_year',
          'total_consumption_previous_year',
          'current_calendar_year_sum',
          'last_calendar_year_sum',
          'average_daily_consumption',
          'yearly_trend',
          'yearly_trend_active',
          'yearly_trend_inactive',
          'yoy_ratio',
          'yoy_ratio_current_calendar_year_vs_last',
          'yoy_ratio_weekdays',
          'yoy_ratio_weekends',
          'yoy_ratio_weekday_nights',
          'inactive_active_ratio',
          'weekend_weekday_ratio',
          'night_day_ratio',
          'previous_year_ratio',
          'previous_year_ratio_weekdays',
          'previous_year_ratio_weekends',
          'previous_year_ratio_weekday_nights',
          'weekend_night_weekday_night_ratio',
          'peak',
          'average_total_price',
          'average_nighttime_price',
          'total_cost_year',
          'total_cost_last_year',
          'total_cost_previous_year',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'average_all' },
              { field: 'average_all_last_year' },
              { field: 'current_calendar_year_avg' },
              { field: 'last_calendar_year_avg' },
              { field: 'average_last_3_years' },
              { field: 'average_17_to_21' },
              { field: 'average_all_inactive_hours' },
              { field: 'average_weekday_full_day' },
              { field: 'average_weekend_full_day' },
              { field: 'average_weekday_day' },
              { field: 'average_weekend_day' },
              { field: 'average_weekday_night' },
              { field: 'average_weekend_night' },
            ],
          },
          {
            groupId: 'yearlyTotal',
            description: t('columnGroups.yearlyTotal.description'),
            headerName: t('columnGroups.yearlyTotal.header'),
            children: [
              { field: 'total_consumption_year' },
              { field: 'total_consumption_last_year' },
              { field: 'total_consumption_previous_year' },
              { field: 'current_calendar_year_sum' },
              { field: 'last_calendar_year_sum' },
              { field: 'average_daily_consumption' },
            ],
          },
          {
            groupId: 'yearly_trend',
            description: t('columnGroups.yearly_trend.description'),
            headerName: t('columnGroups.yearly_trend.header'),
            children: [
              { field: 'yearly_trend' },
              { field: 'yearly_trend_active' },
              { field: 'yearly_trend_inactive' },
            ],
          },
          {
            groupId: 'yoy',
            description: t('columnGroups.yoy.description'),
            headerName: t('columnGroups.yoy.header'),
            children: [
              { field: 'yoy_ratio' },
              { field: 'yoy_ratio_current_calendar_year_vs_last' },
              { field: 'yoy_ratio_weekdays' },
              { field: 'yoy_ratio_weekends' },
              { field: 'yoy_ratio_weekday_nights' },
            ],
          },
          {
            groupId: 'consumptionRatio',
            description: t('columnGroups.consumptionRatio.description'),
            headerName: t('columnGroups.consumptionRatio.header'),
            children: [
              { field: 'inactive_active_ratio' },
              { field: 'weekend_weekday_ratio' },
              { field: 'night_day_ratio' },
              { field: 'previous_year_ratio' },
              { field: 'previous_year_ratio_weekdays' },
              { field: 'previous_year_ratio_weekends' },
              { field: 'previous_year_ratio_weekday_nights' },
              { field: 'weekend_night_weekday_night_ratio' },
            ],
          },
          {
            groupId: 'peak',
            headerName: t('columnGroups.peak.header'),
            description: t('columnGroups.peak.description'),
            children: [{ field: 'peak' }],
          },
          {
            groupId: 'averagePrice',
            headerName: t('columnGroups.averagePrice.header'),
            description: t('columnGroups.averagePrice.description'),
            children: [
              { field: 'average_total_price' },
              { field: 'average_nighttime_price' },
            ],
          },
          {
            groupId: 'yearlyTotalPrice',
            description: t('columnGroups.yearlyTotalPrice.description'),
            headerName: t('columnGroups.yearlyTotalPrice.header'),
            children: [
              { field: 'total_cost_year' },
              { field: 'total_cost_last_year' },
              { field: 'total_cost_previous_year' },
            ],
          },
        ],
      },
    ];
  if (analysisType === 'heat')
    return [
      {
        name: t('presets.overview'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'last_30_days_average',
          'average_heating_season',
          'average_non_heating_season',
          'total_consumption_year',
          'total_consumption_last_year',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'last_30_days_average' },
              { field: 'average_heating_season' },
              { field: 'average_non_heating_season' },
            ],
          },
          {
            groupId: 'yearlyTotal',
            description: t('columnGroups.yearlyTotal.description'),
            headerName: t('columnGroups.yearlyTotal.header'),
            children: [
              { field: 'total_consumption_year' },
              { field: 'total_consumption_last_year' },
            ],
          },
        ],
      },
      {
        name: t('presets.dailyAnalysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'last_30_days_average',
          'average_heating_season',
          'average_heating_season_active',
          'average_heating_season_inactive',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'last_30_days_average' },
              { field: 'average_heating_season' },
              { field: 'average_heating_season_active' },
              { field: 'average_heating_season_inactive' },
            ],
          },
        ],
      },
      {
        name: t('presets.trendAnalysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'last_30_days_average',
          'last_30_day_trend',
          'heating_season_trend',
          'heating_season_trend_active',
          'heating_season_trend_inactive',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [{ field: 'last_30_days_average' }],
          },
          {
            groupId: 'yearly_trend',
            headerName: t('columnGroups.yearly_trend.header'),
            description: t('columnGroups.yearly_trend.description'),
            children: [
              { field: 'last_30_day_trend' },
              { field: 'heating_season_trend' },
              { field: 'heating_season_trend_active' },
              { field: 'heating_season_trend_inactive' },
            ],
          },
        ],
      },
      {
        name: t('presets.calendar_totals'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'previous_calendar_year_sum',
          'previous_calendar_year_consumption_pr_m2_pr_y',
          'last_calendar_year_sum',
          'last_calendar_year_consumption_pr_m2_pr_y',
          'current_calendar_year_sum',
          'current_calendar_year_consumption_pr_m2_pr_y',
          'warnings',
        ],
        sortingModel: [{ field: GRID_TREE_DATA_GROUPING_FIELD, sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'previous_calendar_year',
            description: t('columnGroups.previous_calendar_year.description'),
            headerName: previous_year
              ? previous_year.toString()
              : t('columnGroups.previous_calendar_year.header'),
            children: [
              { field: 'previous_calendar_year_sum' },
              { field: 'previous_calendar_year_consumption_pr_m2_pr_y' },
            ],
          },
          {
            groupId: 'last_calendar_year',
            description: t('columnGroups.last_calendar_year.description'),
            headerName: last_year
              ? last_year.toString()
              : t('columnGroups.last_calendar_year.header'),
            children: [
              { field: 'last_calendar_year_sum' },
              { field: 'last_calendar_year_consumption_pr_m2_pr_y' },
            ],
          },
          {
            groupId: 'current_calendar_year',
            description: t('columnGroups.current_calendar_year.description'),
            headerName: current_year
              ? current_year.toString()
              : t('columnGroups.current_calendar_year.header'),
            children: [
              { field: 'current_calendar_year_sum' },
              { field: 'current_calendar_year_consumption_pr_m2_pr_y' },
            ],
          },
        ],
      },
      {
        name: t('presets.temperature_analysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'forward_flow_temp_avg_last_30_days',
          'return_flow_temp_avg_last_30_days',
          'delta_temp_avg_last_30_days',

          'forward_flow_temp_avg_year',
          'return_flow_temp_avg_year',
          'delta_temp_avg_year',
          'delta_temp_trend_last_30_days',
          'delta_temp_trend_year',
          'warnings',
        ],
        sortingModel: [{ field: 'delta_temp_avg_last_30_days', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },

        columnGroupingModel: [
          {
            groupId: 'temps_last_30_days',
            description: t('columnGroups.temps_last_30_days.description'),
            headerName: t('columnGroups.temps_last_30_days.header'),

            children: [
              { field: 'forward_flow_temp_avg_last_30_days' },
              { field: 'return_flow_temp_avg_last_30_days' },
              { field: 'delta_temp_avg_last_30_days' },
            ],
          },
          {
            groupId: 'temps_last_year',
            description: t('columnGroups.temps_last_year.description'),
            headerName: t('columnGroups.temps_last_year.header'),
            children: [
              { field: 'forward_flow_temp_avg_year' },
              { field: 'return_flow_temp_avg_year' },
              { field: 'delta_temp_avg_year' },
            ],
          },
          {
            groupId: 'delta_temperature',
            description: t('columnGroups.delta_temperature.description'),
            headerName: t('columnGroups.delta_temperature.header'),
            children: [
              { field: 'delta_temp_trend_last_30_days' },
              { field: 'delta_temp_trend_year' },
            ],
          },
        ],
      },
      {
        name: t('presets.price_analysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'average_total_price',
          'average_nighttime_price',
          'total_cost_year',
          'total_cost_last_year',
          'total_cost_previous_year',
          'warnings',
        ],
        sortingModel: [{ field: 'average_total_price', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averagePrice',
            headerName: t('columnGroups.averagePrice.header'),
            description: t('columnGroups.averagePrice.description'),
            children: [
              { field: 'average_total_price' },
              { field: 'average_nighttime_price' },
            ],
          },
          {
            groupId: 'yearlyTotalPrice',
            description: t('columnGroups.yearlyTotalPrice.description'),
            headerName: t('columnGroups.yearlyTotalPrice.header'),
            children: [
              { field: 'total_cost_year' },
              { field: 'total_cost_last_year' },
              { field: 'total_cost_previous_year' },
            ],
          },
        ],
      },
      {
        name: t('presets.detailed'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'occupancy',
          'daytime_start',
          'daytime_end',
          'last_30_days_average',
          'average_heating_season',
          'average_non_heating_season',
          'average_heating_season_active',
          'average_heating_season_inactive',
          'average_all',
          'average_all_last_year',
          'current_calendar_year_avg',
          'last_calendar_year_avg',
          'average_last_3_years',
          'average_all_inactive_hours',
          'average_weekday_full_day',
          'average_weekend_full_day',
          'average_weekday_day',
          'average_weekend_day',
          'average_weekday_night',
          'average_weekend_night',
          'total_consumption_year',
          'total_consumption_last_year',
          'total_consumption_previous_year',
          'current_calendar_year_sum',
          'last_calendar_year_sum',
          'average_daily_consumption',
          'yearly_trend',
          'yearly_trend_active',
          'yearly_trend_inactive',
          'heating_season_trend',
          'heating_season_trend_active',
          'heating_season_trend_inactive',
          'yoy_ratio',
          'yoy_ratio_current_calendar_year_vs_last',
          'yoy_ratio_weekdays',
          'yoy_ratio_weekends',
          'yoy_ratio_weekday_nights',
          'inactive_active_ratio',
          'weekend_weekday_ratio',
          'night_day_ratio',
          'previous_year_ratio',
          'previous_year_ratio_weekdays',
          'previous_year_ratio_weekends',
          'previous_year_ratio_weekday_nights',
          'weekend_night_weekday_night_ratio',
          'peak',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'last_30_days_average' },
              { field: 'average_heating_season' },
              { field: 'average_non_heating_season' },
              { field: 'average_heating_season_active' },
              { field: 'average_heating_season_inactive' },
              { field: 'average_all' },
              { field: 'average_all_last_year' },
              { field: 'current_calendar_year_avg' },
              { field: 'last_calendar_year_avg' },
              { field: 'average_last_3_years' },
              { field: 'average_all_inactive_hours' },
              { field: 'average_weekday_full_day' },
              { field: 'average_weekend_full_day' },
              { field: 'average_weekday_day' },
              { field: 'average_weekend_day' },
              { field: 'average_weekday_night' },
              { field: 'average_weekend_night' },
            ],
          },
          {
            groupId: 'yearlyTotal',
            description: t('columnGroups.yearlyTotal.description'),
            headerName: t('columnGroups.yearlyTotal.header'),
            children: [
              { field: 'total_consumption_year' },
              { field: 'total_consumption_last_year' },
              { field: 'total_consumption_previous_year' },
              { field: 'current_calendar_year_sum' },
              { field: 'last_calendar_year_sum' },
              { field: 'average_daily_consumption' },
            ],
          },
          {
            groupId: 'yearly_trend',
            description: t('columnGroups.yearly_trend.description'),
            headerName: t('columnGroups.yearly_trend.header'),
            children: [
              { field: 'yearly_trend' },
              { field: 'yearly_trend_active' },
              { field: 'yearly_trend_inactive' },
              { field: 'heating_season_trend' },
              { field: 'heating_season_trend_active' },
              { field: 'heating_season_trend_inactive' },
            ],
          },
          {
            groupId: 'yoy',
            description: t('columnGroups.yoy.description'),
            headerName: t('columnGroups.yoy.header'),
            children: [
              { field: 'yoy_ratio' },
              { field: 'yoy_ratio_current_calendar_year_vs_last' },
              { field: 'yoy_ratio_weekdays' },
              { field: 'yoy_ratio_weekends' },
              { field: 'yoy_ratio_weekday_nights' },
            ],
          },
          {
            groupId: 'consumptionRatio',
            description: t('columnGroups.consumptionRatio.description'),
            headerName: t('columnGroups.consumptionRatio.header'),
            children: [
              { field: 'inactive_active_ratio' },
              { field: 'weekend_weekday_ratio' },
              { field: 'night_day_ratio' },
              { field: 'previous_year_ratio' },
              { field: 'previous_year_ratio_weekdays' },
              { field: 'previous_year_ratio_weekends' },
              { field: 'previous_year_ratio_weekday_nights' },
              { field: 'weekend_night_weekday_night_ratio' },
            ],
          },
          {
            groupId: 'peak',
            headerName: t('columnGroups.peak.header'),
            description: t('columnGroups.peak.description'),
            children: [{ field: 'peak' }],
          },
        ],
      },
    ];
  if (analysisType === 'water')
    return [
      {
        name: t('presets.overview'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'last_30_days_average',
          'average_all',
          'average_weekday_daytime',
          'average_all_inactive_hours',
          'total_consumption_year',
          'total_consumption_last_year',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'last_30_days_average' },
              { field: 'average_all' },
              { field: 'average_weekday_daytime' },
              { field: 'average_all_inactive_hours' },
            ],
          },
          {
            groupId: 'yearlyTotal',
            description: t('columnGroups.yearlyTotal.description'),
            headerName: t('columnGroups.yearlyTotal.header'),
            children: [
              { field: 'total_consumption_year' },
              { field: 'total_consumption_last_year' },
            ],
          },
        ],
      },
      {
        name: t('presets.dailyAnalysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'last_30_days_average',
          'average_all',
          'average_weekday_day',
          'average_all_inactive_hours',
          'inactive_active_ratio',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [
            {
              field: 'average_all',
              operator: '>=',
              value: getTop15Value(transformedData.portfolio_analysis_list),
            },
          ],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'last_30_days_average' },
              { field: 'average_all' },
              { field: 'average_weekday_day' },
              { field: 'average_all_inactive_hours' },
            ],
          },
          {
            groupId: 'consumptionRatio',
            headerName: t('columnGroups.consumptionRatio.header'),
            description: t('columnGroups.consumptionRatio.description'),
            children: [{ field: 'inactive_active_ratio' }],
          },
        ],
      },
      {
        name: t('presets.trendAnalysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'last_30_days_average',
          'average_all',
          'yearly_trend',
          'yearly_trend_active',
          'yearly_trend_inactive',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'last_30_days_average' },
              { field: 'average_all' },
            ],
          },
          {
            groupId: 'yearly_trend',
            headerName: t('columnGroups.yearly_trend.header'),
            description: t('columnGroups.yearly_trend.description'),
            children: [
              { field: 'yearly_trend' },
              { field: 'yearly_trend_active' },
              { field: 'yearly_trend_inactive' },
            ],
          },
        ],
      },
      {
        name: t('presets.calendar_totals'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'previous_calendar_year_sum',
          'previous_calendar_year_consumption_pr_m2_pr_y',
          'last_calendar_year_sum',
          'last_calendar_year_consumption_pr_m2_pr_y',
          'current_calendar_year_sum',
          'current_calendar_year_consumption_pr_m2_pr_y',
          'warnings',
        ],
        sortingModel: [{ field: GRID_TREE_DATA_GROUPING_FIELD, sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'previous_calendar_year',
            description: t('columnGroups.previous_calendar_year.description'),
            headerName: previous_year
              ? previous_year.toString()
              : t('columnGroups.previous_calendar_year.header'),
            children: [
              { field: 'previous_calendar_year_sum' },
              { field: 'previous_calendar_year_consumption_pr_m2_pr_y' },
            ],
          },
          {
            groupId: 'last_calendar_year',
            description: t('columnGroups.last_calendar_year.description'),
            headerName: last_year
              ? last_year.toString()
              : t('columnGroups.last_calendar_year.header'),
            children: [
              { field: 'last_calendar_year_sum' },
              { field: 'last_calendar_year_consumption_pr_m2_pr_y' },
            ],
          },
          {
            groupId: 'current_calendar_year',
            description: t('columnGroups.current_calendar_year.description'),
            headerName: current_year
              ? current_year.toString()
              : t('columnGroups.current_calendar_year.header'),
            children: [
              { field: 'current_calendar_year_sum' },
              { field: 'current_calendar_year_consumption_pr_m2_pr_y' },
            ],
          },
        ],
      },
      {
        name: t('presets.temperature_analysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'forward_flow_temp_avg_last_30_days',
          'return_flow_temp_avg_last_30_days',
          'forward_flow_temp_avg_year',
          'return_flow_temp_avg_year',
          'delta_temp_avg_last_30_days',
          'delta_temp_avg_year',
          'delta_temp_trend_last_30_days',
          'delta_temp_trend_year',
          'warnings',
        ],
        sortingModel: [{ field: 'delta_temp_avg_last_30_days', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },

        columnGroupingModel: [
          {
            groupId: 'delta_temperature',
            description: t('columnGroups.delta_temperature.description'),
            headerName: t('columnGroups.delta_temperature.header'),
            children: [
              { field: 'delta_temp_avg_last_30_days' },
              { field: 'delta_temp_avg_year' },
              { field: 'delta_temp_trend_last_30_days' },
              { field: 'delta_temp_trend_year' },
            ],
          },
        ],
      },
      {
        name: t('presets.price_analysis'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'average_total_price',
          'average_nighttime_price',
          'total_cost_year',
          'total_cost_last_year',
          'total_cost_previous_year',
          'warnings',
        ],
        sortingModel: [{ field: 'average_total_price', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averagePrice',
            headerName: t('columnGroups.averagePrice.header'),
            description: t('columnGroups.averagePrice.description'),
            children: [
              { field: 'average_total_price' },
              { field: 'average_nighttime_price' },
            ],
          },
          {
            groupId: 'yearlyTotalPrice',
            description: t('columnGroups.yearlyTotalPrice.description'),
            headerName: t('columnGroups.yearlyTotalPrice.header'),
            children: [
              { field: 'total_cost_year' },
              { field: 'total_cost_last_year' },
              { field: 'total_cost_previous_year' },
            ],
          },
        ],
      },
      {
        name: t('presets.detailed'),
        order: [
          'favorites',
          'type',
          'linked_energy_groups',

          'occupancy',
          'daytime_start',
          'daytime_end',
          'last_30_days_average',
          'average_all',
          'average_all_last_year',
          'current_calendar_year_avg',
          'last_calendar_year_avg',
          'average_last_3_years',
          'average_all_inactive_hours',
          'average_weekday_full_day',
          'average_weekend_full_day',
          'average_weekday_day',
          'average_weekend_day',
          'average_weekday_night',
          'average_weekend_night',
          'total_consumption_year',
          'total_consumption_last_year',
          'total_consumption_previous_year',
          'current_calendar_year_sum',
          'last_calendar_year_sum',
          'average_daily_consumption',
          'yearly_trend',
          'yearly_trend_active',
          'yearly_trend_inactive',
          'yoy_ratio',
          'yoy_ratio_current_calendar_year_vs_last',
          'yoy_ratio_weekdays',
          'yoy_ratio_weekends',
          'yoy_ratio_weekday_nights',
          'inactive_active_ratio',
          'weekend_weekday_ratio',
          'night_day_ratio',
          'previous_year_ratio',
          'previous_year_ratio_weekdays',
          'previous_year_ratio_weekends',
          'previous_year_ratio_weekday_nights',
          'weekend_night_weekday_night_ratio',
          'peak',
          'warnings',
        ],
        sortingModel: [{ field: 'average_all', sort: 'desc' }],
        rowGrouping: {
          model: ['matrikel_key'],
        },
        filterModel: {
          items: [],
        },
        columnGroupingModel: [
          {
            groupId: 'activityPatterns',
            headerName: t('columnGroups.activityPatterns.header'),
            description: t('columnGroups.activityPatterns.description'),
            children: [
              { field: 'occupancy' },
              { field: 'daytime_start' },
              { field: 'daytime_end' },
            ],
          },
          {
            groupId: 'averageConsumption',
            headerName: t('columnGroups.averageConsumption.header'),
            description: t('columnGroups.averageConsumption.description'),
            children: [
              { field: 'last_30_days_average' },
              { field: 'average_all' },
              { field: 'average_all_last_year' },
              { field: 'current_calendar_year_avg' },
              { field: 'last_calendar_year_avg' },
              { field: 'average_last_3_years' },
              { field: 'average_all_inactive_hours' },
              { field: 'average_weekday_full_day' },
              { field: 'average_weekend_full_day' },
              { field: 'average_weekday_day' },
              { field: 'average_weekend_day' },
              { field: 'average_weekday_night' },
              { field: 'average_weekend_night' },
            ],
          },
          {
            groupId: 'yearlyTotal',
            description: t('columnGroups.yearlyTotal.description'),
            headerName: t('columnGroups.yearlyTotal.header'),
            children: [
              { field: 'total_consumption_year' },
              { field: 'total_consumption_last_year' },
              { field: 'total_consumption_previous_year' },
              { field: 'current_calendar_year_sum' },
              { field: 'last_calendar_year_sum' },
              { field: 'average_daily_consumption' },
            ],
          },
          {
            groupId: 'yearly_trend',
            description: t('columnGroups.yearly_trend.description'),
            headerName: t('columnGroups.yearly_trend.header'),
            children: [
              { field: 'yearly_trend' },
              { field: 'yearly_trend_active' },
              { field: 'yearly_trend_inactive' },
            ],
          },
          {
            groupId: 'yoy',
            description: t('columnGroups.yoy.description'),
            headerName: t('columnGroups.yoy.header'),
            children: [
              { field: 'yoy_ratio' },
              { field: 'yoy_ratio_current_calendar_year_vs_last' },
              { field: 'yoy_ratio_weekdays' },
              { field: 'yoy_ratio_weekends' },
              { field: 'yoy_ratio_weekday_nights' },
            ],
          },
          {
            groupId: 'consumptionRatio',
            description: t('columnGroups.consumptionRatio.description'),
            headerName: t('columnGroups.consumptionRatio.header'),
            children: [
              { field: 'inactive_active_ratio' },
              { field: 'weekend_weekday_ratio' },
              { field: 'night_day_ratio' },
              { field: 'previous_year_ratio' },
              { field: 'previous_year_ratio_weekdays' },
              { field: 'previous_year_ratio_weekends' },
              { field: 'previous_year_ratio_weekday_nights' },
              { field: 'weekend_night_weekday_night_ratio' },
            ],
          },
          {
            groupId: 'peak',
            headerName: t('columnGroups.peak.header'),
            description: t('columnGroups.peak.description'),
            children: [{ field: 'peak' }],
          },
        ],
      },
    ];
};
